import { Head, router } from "@inertiajs/react";
import { ArrowRight, Check, Copy, ExternalLink, Key, Loader2 } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";

import { OnboardingLayout } from "@/components/onboarding/onboarding-layout";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";

type StepKey = "connect_domain" | "sign_up" | "branding" | "api_key";

type StepStatus = "pending" | "completed" | "skipped";

interface Domain {
  id: number;
  hostname: string;
  verification_status?: "unverified" | "verified" | "pending" | "failed";
}

interface ExistingApiKey {
  id: number;
  name: string;
  tokenPrefix: string;
}

interface OnboardingState {
  currentStep: StepKey;
  steps: { key: StepKey; status: StepStatus; completedAt?: string }[];
}

interface Props {
  domains: Domain[];
  existingApiKey: ExistingApiKey | null;
  newApiKeyToken: string | null;
  onboarding: OnboardingState;
}

export default function OnboardingApiKey({ domains, existingApiKey, newApiKeyToken, onboarding }: Props) {
  const [copiedToken, setCopiedToken] = useState(false);
  const [isCreating, setIsCreating] = useState(false);

  const handleCreateApiKey = () => {
    setIsCreating(true);
    router.post(
      "/onboarding/api_key",
      {},
      {
        preserveScroll: true,
        onFinish: () => setIsCreating(false),
      },
    );
  };

  const handleCopyToken = async () => {
    if (!newApiKeyToken) return;
    await navigator.clipboard.writeText(newApiKeyToken);
    setCopiedToken(true);
    toast.success("API key copied to clipboard");
    setTimeout(() => setCopiedToken(false), 2000);
  };

  const handleSkip = () => {
    router.post("/onboarding/steps/api_key/skip");
  };

  const handleContinue = () => {
    router.visit("/dashboard");
  };

  const hasApiKey = !!existingApiKey || !!newApiKeyToken;
  const verifiedDomains = domains.filter((domain) => domain.verification_status === "verified");
  const hasVerifiedDomains = verifiedDomains.length > 0;
  const primaryDomain = verifiedDomains[0] || domains[0];

  return (
    <OnboardingLayout
      title="Generate Your API Key"
      description="Create an API key to start generating OG images programmatically"
      onboarding={onboarding}
    >
      <Head title="Generate API Key - Onboarding" />

      <div className="mx-auto max-w-2xl space-y-6">
        <div className="text-center">
          <div className="bg-primary/10 mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full">
            <Key className="text-primary h-8 w-8" />
          </div>
          <h1 className="text-3xl font-bold tracking-tight">Generate Your API Key</h1>
          <p className="text-muted-foreground mt-2">Create an API key to start generating OG images programmatically</p>
        </div>

        {/* New API Key Display */}
        {newApiKeyToken && (
          <Alert className="border-green-200 bg-green-50 dark:border-green-800 dark:bg-green-950">
            <Key className="h-4 w-4 text-green-600" />
            <AlertTitle className="text-green-800 dark:text-green-200">🎉 API Key Created!</AlertTitle>
            <AlertDescription className="space-y-3">
              <p className="text-green-700 dark:text-green-300">
                Copy your API key now. You won&apos;t be able to see it again!
              </p>
              <div className="flex items-center gap-2">
                <code className="flex-1 rounded bg-green-100 px-3 py-2 font-mono text-sm break-all dark:bg-green-900">
                  {newApiKeyToken}
                </code>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    void handleCopyToken();
                  }}
                  className="shrink-0"
                >
                  {copiedToken ? <Check className="h-4 w-4 text-green-600" /> : <Copy className="h-4 w-4" />}
                </Button>
              </div>
            </AlertDescription>
          </Alert>
        )}

        {/* Existing API Key */}
        {existingApiKey && !newApiKeyToken && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Check className="h-5 w-5 text-green-600" />
                You already have an API key
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-2">
                <span className="text-muted-foreground">Key:</span>
                <code className="bg-muted rounded px-2 py-1 text-sm">{existingApiKey.tokenPrefix}••••••••</code>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Create API Key Card */}
        {!hasApiKey && (
          <Card>
            <CardHeader>
              <CardTitle>Create API Key</CardTitle>
              <CardDescription>This key works across all verified domains on your account.</CardDescription>
            </CardHeader>
            <CardContent>
              <Button onClick={handleCreateApiKey} disabled={isCreating} className="w-full">
                {isCreating ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Key className="mr-2 h-4 w-4" />}
                Generate API Key
              </Button>
            </CardContent>
          </Card>
        )}

        {!hasVerifiedDomains && domains.length > 0 && (
          <Alert className="border-amber-200 bg-amber-50 text-amber-900">
            <AlertTitle>No verified domains yet</AlertTitle>
            <AlertDescription>
              Verify at least one domain before the API can generate OG images. You can complete verification in the
              domain setup step.
            </AlertDescription>
          </Alert>
        )}

        {/* Usage Instructions */}
        <Card>
          <CardHeader>
            <CardTitle>How to Use Your API Key</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <h4 className="mb-2 font-medium">1. Sign a JWT payload</h4>
              <pre className="bg-muted overflow-x-auto rounded-lg p-4 text-sm">
                {`{\n  "iss": "${primaryDomain?.hostname || "yourdomain.com"}",\n  "sub": "YOUR_KEY_PREFIX",\n  "title": "My Page Title"\n}`}
              </pre>
              <p className="text-muted-foreground text-sm">
                Use the first 8 characters of your API key for <code>sub</code>, then sign with HS256 using your API key
                as the secret. The <code>iss</code> claim must be one of your verified domains.
              </p>
            </div>

            <div>
              <h4 className="mb-2 font-medium">2. Call the API server-side</h4>
              <pre className="bg-muted overflow-x-auto rounded-lg p-4 text-sm">
                {`curl -X POST "https://ogpilot.com/api/v1/images" \\
  -H "Content-Type: application/json" \\
  -H "Accept: application/json" \\
  -d '{"token":"YOUR_JWT_TOKEN"}'`}
              </pre>
            </div>

            <div>
              <h4 className="mb-2 font-medium">3. Use the returned URL in your HTML</h4>
              <pre className="bg-muted overflow-x-auto rounded-lg p-4 text-sm">
                {`<meta property="og:image" content="https://cdn.ogpilot.com/your-generated-image.png" />`}
              </pre>
            </div>

            <Button variant="outline" className="gap-2" onClick={() => window.open("/docs", "_blank")}>
              <ExternalLink className="h-4 w-4" />
              View Full Documentation
            </Button>
          </CardContent>
        </Card>

        {/* Actions */}
        <div className="flex justify-between">
          <Button variant="ghost" onClick={handleSkip}>
            Skip for now
          </Button>
          <Button onClick={handleContinue} disabled={!hasApiKey}>
            Continue to Dashboard
            <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
        </div>
      </div>
    </OnboardingLayout>
  );
}
